import {
  ApiClient,
  Entities_Login_ByUserIdAndPassword_UserInfo,
  Entities_Login_StateInfo_Response,
  PlayerId,
  UserId,
} from '@suntex.fim/api-client';
import { LoginType } from '@suntex.fim/enums';

/**
 * Authentication configuration
 */
export interface AuthConfig {
  /** API client for authentication operations */
  apiClient: IAuthApiClient;
  /** Storage adapter for persisting auth data */
  storage?: IAuthStorage;
}

export type BasicLoginInfo = Pick<
  Entities_Login_ByUserIdAndPassword_UserInfo,
  'loginId' | 'loginType'
>;

/**
 * Core authentication state interface
 */
export interface AuthState {
  /** Whether the user is authenticated */
  readonly isAuthenticated: boolean;
  /** Whether authentication is in progress */
  readonly isLoading: boolean;
  /** Whether the user is currently signing out */
  readonly signingOut?: boolean;
  /** Authentication token */
  readonly jwt: string | null;
  /** Current user login information */
  readonly login: Entities_Login_StateInfo_Response | null;

  /** Reason the user was signed out, if not a manual sign-out */
  readonly signOutReason?: SignOutReason;
}

/** Reason a user was automatically signed out */
export enum SignOutReason {
  /** Session expired due to inactivity */
  Expired = 'expired',
}

export interface SignOutOptions {
  /** Why the user is being signed out */
  reason?: SignOutReason;
}

/** Sign in with username and password */
export type SignInWithCredentials = { userId: UserId; password: string };
/** Sign in using a raw JWT (extracts login info from claims) */
export type SignInWithJwt = { jwt: string };
/** Options for the unified signIn action */
export type SignInOptions = SignInWithCredentials | SignInWithJwt;

interface AuthActions {
  refresh: () => Promise<void>;
  /** Sign in with credentials or a raw JWT */
  signIn: (options: SignInOptions) => Promise<AuthState>;
  /** Sign out, optionally specifying a reason */
  signOut: (options?: SignOutOptions) => Promise<AuthState>;
  /** Manually update the player id */
  setPlayerId: (playerId: PlayerId) => void;
}

/**
 * React-specific authentication context type
 */
export type AuthContext = AuthState & AuthActions;

/**
 * Authentication service interface
 */
export interface IAuthService extends AuthActions {
  /** Initialize authentication (check for existing tokens) */
  initialize(): Promise<AuthState>;
  /** Get current authentication state */
  getState(): AuthState;
  /** Subscribe to authentication state changes */
  subscribe(callback: (state: AuthState) => void): () => void;
  /** Clean up background timers. Call when the service instance is no longer needed. */
  dispose(): void;
}

/**
 * Storage adapter interface for persisting auth data
 */
export interface IAuthStorage {
  clear(): void;

  get loginToken(): string | undefined;
  get jwt(): string | undefined;
  get login(): Entities_Login_StateInfo_Response | undefined;

  set loginToken(token: string);
  set jwt(token: string);
  set login(login: Entities_Login_StateInfo_Response);

  /**
   * Subscribe to external storage changes (e.g., from another browser tab).
   * Returns a cleanup function to unsubscribe.
   * Adapters that don't support external change detection may omit this.
   */
  onChange?(callback: () => void): () => void;
}

/**
 * API client interface for authentication operations
 */
export type IAuthApiClient = Pick<
  ApiClient,
  | 'post_Login_ByUserIdAndPassword'
  | 'post_Login_ByToken'
  | 'get_Login_StateInfo'
  | 'get_Login_Refresh'
>;

/**
 * Authentication error types
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public code?: string,
    public originalError?: unknown,
  ) {
    super(message);
    this.name = 'AuthError';
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
    };
  }
}

/**
 * Player type detection utility
 */
export function isPlayerLoginType(loginType: LoginType): boolean {
  return (
    loginType === LoginType.StudentPlayer ||
    loginType === LoginType.IndividualPlayer
  );
}
